/**
 * ClusterAnalysisModal - User-driven progressive analysis workflow
 *
 * Flow:
 * 1. RCA tab: Evidence + [Run Analysis] → Root cause results
 * 2. Simulate tab: Prompt/config comparison + Regression tests + Apply with Claude Code
 * 3. Guard tab: Test issue reproduction + Configure policy (Done)
 *
 * Each step unlocks the next. User drives the flow.
 */
import React, { useState } from 'react';
import {
  X, CheckCircle, XCircle, Clock, Play, Loader2,
  Copy, Check, Zap, FlaskConical, ExternalLink,
  ChevronDown, AlertTriangle, Search, ArrowRight,
  Shield, Mail, MessageSquare, UserCheck
} from 'lucide-react';
import { components, palette, graph, withOpacity } from '../utils/designTokens';
import { getRCAResult, getReproductionConfig, getFixConfig } from '../services/demoDataService';
import { POLICY_ACTIONS } from '../services/mockDemoData';
import MutationTraceView from './MutationTraceView';
import EnhancementTraceView from './EnhancementTraceView';

// Action icon mapping (same as PolicyWizard)
const ACTION_ICONS = {
  'Shield': Shield,
  'AlertTriangle': AlertTriangle,
  'Mail': Mail,
  'MessageSquare': MessageSquare,
  'UserCheck': UserCheck
};

// Status icons for causality chain (with severity-based styling from design tokens)
const statusIcons = {
  success: {
    icon: CheckCircle,
    color: palette.status.success,
    bg: palette.severity.success.bg,
    border: palette.severity.success.border,
    glow: palette.severity.success.glow
  },
  warning: {
    icon: Clock,
    color: palette.status.warning,
    bg: palette.severity.high.bg,
    border: palette.severity.high.border,
    glow: palette.severity.high.glow
  },
  error: {
    icon: XCircle,
    color: palette.status.danger,
    bg: palette.severity.critical.bg,
    border: palette.severity.critical.border,
    glow: palette.severity.critical.glow
  },
  timeout: {
    icon: Clock,
    color: palette.status.warning,
    bg: palette.severity.high.bg,
    border: palette.severity.high.border,
    glow: palette.severity.high.glow
  }
};

// Category badge colors using design tokens (maps to palette.severity)
const categoryColors = {
  caching: {
    bg: palette.severity.low.bg,
    text: palette.severity.low.text,
    border: palette.severity.low.border
  },
  prompt_engineering: {
    bg: palette.severity.medium.bg,
    text: palette.severity.medium.text,
    border: palette.severity.medium.border
  },
  tool_routing: {
    bg: palette.severity.high.bg,
    text: palette.severity.high.text,
    border: palette.severity.high.border
  },
  infrastructure: {
    bg: palette.borders.subtle,
    text: palette.text.muted,
    border: palette.borders.medium
  }
};

// Copyable code block
const CopyableCode = ({ code, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      {label && <div className="text-xs mb-1" style={{ color: palette.text.supporting }}>{label}</div>}
      <div className="flex items-start gap-2 p-3 rounded" style={{ backgroundColor: palette.borders.medium }}>
        <pre
          className="flex-1 text-xs font-mono whitespace-pre-wrap overflow-x-auto"
          style={{ color: palette.text.primary }}
        >
          {code}
        </pre>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-1.5 rounded transition-colors"
          style={{ color: palette.text.supporting }}
          title="Copy to clipboard"
        >
          {copied ? <Check size={14} style={{ color: palette.status.success }} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
};

// Context Panel - Summary + Traces (collapsible with smooth transition)
const ContextPanel = ({ cluster, isExpanded, onToggle }) => {
  const sampleTraces = cluster.sample_traces || [];

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ border: `1px solid ${components.card.borderColor}` }}
    >
      {/* Collapsible Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:opacity-90"
        style={{ backgroundColor: palette.borders.medium }}
      >
        <div className="flex items-center gap-3">
          <div
            className="transition-transform duration-200"
            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <ChevronDown size={16} />
          </div>
          <span className="text-sm font-medium" style={{ color: palette.text.primary }}>
            Context
          </span>
          <span className="text-xs" style={{ color: palette.text.supporting }}>
            {sampleTraces.length} sample traces
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs" style={{ color: palette.text.supporting }}>
          <span>
            Avg Score: <span style={{ color: palette.status.danger }}>{Math.round(cluster.avg_score * 100)}%</span>
          </span>
          <span>
            Span: <code className="px-1 py-0.5 rounded" style={{ backgroundColor: palette.borders.subtle }}>{cluster.affected_span}</code>
          </span>
        </div>
      </button>

      {/* Collapsible Content with smooth transition */}
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{
          gridTemplateRows: isExpanded ? '1fr' : '0fr',
          opacity: isExpanded ? 1 : 0
        }}
      >
        <div className="overflow-hidden">
          <div className="space-y-3 p-4" style={{ backgroundColor: palette.borders.subtle }}>
            {/* Cluster Summary */}
            <p className="text-sm" style={{ color: palette.text.primary }}>
              {cluster.description}
            </p>

            {/* Traces Table */}
            <div
              className="rounded overflow-hidden"
              style={{ border: `1px solid ${components.card.borderColor}` }}
            >
              {/* Table Header */}
              <div
                className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium"
                style={{ backgroundColor: palette.borders.medium, color: palette.text.supporting }}
              >
                <div className="col-span-2">Score</div>
                <div className="col-span-5">Input</div>
                <div className="col-span-5">Output</div>
              </div>
              {/* Table Body - Scrollable */}
              <div className="max-h-40 overflow-y-auto" style={{ backgroundColor: graph.background }}>
                {sampleTraces.map((trace, idx) => (
                  <div
                    key={trace.id}
                    className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-start"
                    style={{
                      borderTop: idx > 0 ? `1px solid ${components.card.borderColor}` : 'none'
                    }}
                  >
                    <div className="col-span-2">
                      <span className="font-semibold" style={{ color: palette.status.danger }}>
                        {Math.round(trace.score * 100)}%
                      </span>
                    </div>
                    <div className="col-span-5" style={{ color: palette.text.supporting }}>
                      <span className="line-clamp-2">"{trace.input}"</span>
                    </div>
                    <div className="col-span-5" style={{ color: palette.text.supporting }}>
                      <span className="line-clamp-2">"{trace.output}"</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// RCA Analysis Steps
const RCA_STEPS = [
  { label: 'Collecting trace data', duration: 1200 },
  { label: 'Analyzing input patterns', duration: 1400 },
  { label: 'Identifying anomalies', duration: 1600 },
  { label: 'Correlating with system state', duration: 1400 },
  { label: 'Generating root cause hypothesis', duration: 1200 }
];

// RCA Tab Content
const RCATabContent = ({ rca, rcaStatus, rcaStep }) => {
  const [showChain, setShowChain] = useState(true);

  // Show placeholder if RCA hasn't been run yet
  if (rcaStatus === 'idle') {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Search size={32} style={{ color: palette.text.supporting }} className="mb-3 opacity-50" />
        <p className="text-sm" style={{ color: palette.text.supporting }}>
          Click "Run Analysis" to identify the root cause
        </p>
      </div>
    );
  }

  // Show loading state with steps
  if (rcaStatus === 'running') {
    return (
      <div className="py-6 space-y-4">
        {RCA_STEPS.map((step, idx) => {
          const isActive = idx === rcaStep;
          const isComplete = idx < rcaStep;
          const isPending = idx > rcaStep;

          return (
            <div
              key={idx}
              className="flex items-center gap-3"
              style={{ opacity: isPending ? 0.4 : 1 }}
            >
              {isComplete ? (
                <CheckCircle size={18} style={{ color: palette.status.success }} />
              ) : isActive ? (
                <Loader2 size={18} className="animate-spin" style={{ color: palette.accents.primary.base }} />
              ) : (
                <div
                  className="w-[18px] h-[18px] rounded-full border-2"
                  style={{ borderColor: palette.borders.medium }}
                />
              )}
              <span
                className="text-sm"
                style={{
                  color: isComplete ? palette.status.success : isActive ? palette.text.primary : palette.text.muted
                }}
              >
                {step.label}
                {isActive && '...'}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Show RCA results
  if (!rca) return null;

  const categoryStyle = categoryColors[rca.root_cause.category] || categoryColors.infrastructure;

  return (
    <div className="space-y-4">
      {/* Root Cause Summary - Prominent with glow */}
      <div
        className="p-4 rounded-lg"
        style={{
          backgroundColor: palette.neutrals.canvas,
          border: `2px solid ${palette.severity.critical.border}`,
          boxShadow: palette.severity.critical.glow
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="p-1.5 rounded"
              style={{ backgroundColor: withOpacity(palette.status.danger, 10) }}
            >
              <AlertTriangle size={16} style={{ color: palette.status.danger }} />
            </div>
            <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: palette.status.danger }}>
              Root Cause Identified
            </h3>
          </div>
          <span
            className="px-2 py-1 rounded text-xs font-medium"
            style={{
              backgroundColor: categoryStyle.bg,
              color: categoryStyle.text,
              border: `1px solid ${categoryStyle.border}`
            }}
          >
            {rca.root_cause.category.replace(/_/g, ' ')}
          </span>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: palette.text.primary, fontWeight: 500 }}>
          {rca.root_cause.summary}
        </p>
        <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${palette.severity.critical.border}` }}>
          <span className="text-xs" style={{ color: palette.text.supporting }}>Confidence</span>
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: palette.borders.medium }}>
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.round(rca.root_cause.confidence * 100)}%`,
                backgroundColor: palette.status.danger
              }}
            />
          </div>
          <span className="text-xs font-semibold" style={{ color: palette.status.danger }}>
            {Math.round(rca.root_cause.confidence * 100)}%
          </span>
        </div>
      </div>

      {/* Causality Chain - Visual flow */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: `1px solid ${palette.borders.medium}` }}
      >
        <button
          onClick={() => setShowChain(!showChain)}
          className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:opacity-90"
          style={{ backgroundColor: palette.neutrals.panelSecondary }}
        >
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: palette.text.primary }}>
              Causality Chain
            </h3>
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{ backgroundColor: palette.neutrals.surfaceMid, color: palette.text.primary }}
            >
              {rca.causality_chain.length} steps
            </span>
          </div>
          <div
            className="transition-transform duration-200"
            style={{ transform: showChain ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <ChevronDown size={14} style={{ color: palette.text.supporting }} />
          </div>
        </button>
        <div
          className="grid transition-all duration-300 ease-in-out"
          style={{
            gridTemplateRows: showChain ? '1fr' : '0fr',
            opacity: showChain ? 1 : 0
          }}
        >
          <div className="overflow-hidden">
            <div className="p-4" style={{ backgroundColor: palette.neutrals.canvas }}>
              <div className="flex items-center gap-1 flex-wrap">
                {rca.causality_chain.map((step, idx) => {
                  const statusConfig = statusIcons[step.status] || statusIcons.error;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <React.Fragment key={step.step}>
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                        style={{
                          backgroundColor: statusConfig.bg,
                          border: `1px solid ${statusConfig.border}`,
                          boxShadow: step.status === 'error' ? statusConfig.glow : 'none'
                        }}
                        title={step.error_message || JSON.stringify(step.details)}
                      >
                        <StatusIcon size={14} style={{ color: statusConfig.color }} />
                        <span style={{ color: palette.text.primary, fontWeight: step.status === 'error' ? 600 : 400 }}>
                          {step.component}
                        </span>
                      </div>
                      {idx < rca.causality_chain.length - 1 && (
                        <ArrowRight size={16} style={{ color: palette.text.muted, flexShrink: 0 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contributing Factors */}
      <div>
        <h3 className="text-xs uppercase mb-2" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
          Contributing Factors
        </h3>
        <ul className="space-y-1.5">
          {rca.contributing_factors.map((factor, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: palette.accents.warm.base }} />
              <span className="text-sm" style={{ color: palette.text.supporting }}>{factor}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Sub-tab button for Reproduce section
const SubTabButton = ({ label, isActive, onClick, icon: Icon, badge }) => {
  // Determine badge color: FAIL=red, PASS=green, number=neutral
  const getBadgeStyle = () => {
    if (badge === 'FAIL') {
      return { backgroundColor: palette.status.danger, color: palette.text.inverse };
    }
    if (badge === 'PASS') {
      return { backgroundColor: palette.status.success, color: palette.text.inverse };
    }
    // Number (count) - use neutral accent
    return { backgroundColor: palette.accents.primary.base, color: palette.text.inverse };
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-2.5 py-1 rounded-full text-sm font-medium transition-colors ${
        isActive
          ? 'bg-accent-primary-base text-text-inverse'
          : 'bg-background-tertiary text-text-supporting hover:bg-background-quaternary'
      }`}
    >
      {Icon && <Icon size={14} />}
      {label}
      {badge && (
        <span
          className="px-1.5 py-0.5 rounded text-xs"
          style={getBadgeStyle()}
        >
          {badge}
        </span>
      )}
    </button>
  );
};

// Reproduce Tab Content - Tabbed interface with Configure Policy and Test tabs
const ReproduceTabContent = ({
  reproConfig,
  clusterId,
  selectedActions,
  actionConfigs,
  onToggleAction,
  onUpdateActionConfig,
  policyEnabled,
  onTogglePolicyEnabled,
  testStatus,
  testResult,
  onRunTest,
  activeSubTab,
  onSubTabChange
}) => {

  if (!reproConfig) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <FlaskConical size={32} style={{ color: palette.text.supporting }} className="mb-3 opacity-50" />
        <p className="text-sm" style={{ color: palette.text.supporting }}>
          No reproduction configuration available
        </p>
      </div>
    );
  }

  const { mutation_config, run_command, test_case, assertions, suggested_policy } = reproConfig;

  // Get default actions from suggested policy
  const defaultActionTypes = suggested_policy?.default_actions?.map(a => a.type) || [];

  return (
    <div className="space-y-3">
      {/* Intro text */}
      <div
        className="px-3 py-2 rounded-lg text-xs"
        style={{ backgroundColor: palette.borders.subtle, color: palette.text.supporting }}
      >
        <strong style={{ color: palette.text.primary }}>Despite the fix, this situation might happen again.</strong>{' '}
        Test the issue first, then configure a policy to guard against it.
      </div>

      {/* Sub-tabs */}
      <div
        className="flex gap-2 mb-4"
      >
        <SubTabButton
          label="Test"
          icon={FlaskConical}
          isActive={activeSubTab === 'test'}
          onClick={() => onSubTabChange('test')}
          badge={testResult ? (testResult === 'pass' ? 'PASS' : 'FAIL') : null}
        />
        <SubTabButton
          label="Configure Policy"
          icon={Zap}
          isActive={activeSubTab === 'configure'}
          onClick={() => onSubTabChange('configure')}
          badge={selectedActions.length > 0 ? `${selectedActions.length}` : null}
        />
      </div>

      {/* Sub-tab Content */}
      <div
        className="p-3 rounded-b-lg"
        style={{ backgroundColor: graph.background, border: `1px solid ${components.card.borderColor}`, borderTop: 'none' }}
      >
        {/* Configure Policy Tab */}
        {activeSubTab === 'configure' && (
          <div className="space-y-3">
            {/* Policy Enforcement Details - inline */}
            <div
              className="px-3 py-2 rounded-lg flex items-center gap-4 flex-wrap"
              style={{ backgroundColor: palette.borders.subtle, border: `1px solid ${components.card.borderColor}` }}
            >
              <div className="flex items-center gap-2 text-[10px] uppercase" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
                <Shield size={10} />
                Policy
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px]" style={{ color: palette.text.supporting }}>Target:</span>
                <code
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: palette.borders.medium, color: palette.accents.primary.base }}
                >
                  {mutation_config?.span_path}
                </code>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px]" style={{ color: palette.text.supporting }}>Condition:</span>
                <code
                  className="text-xs px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: palette.borders.medium, color: palette.accents.warm.base }}
                >
                  {suggested_policy?.metric?.type} {suggested_policy?.metric?.comparator} {suggested_policy?.metric?.threshold}
                </code>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase mb-1.5" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
                Select Actions
              </div>
              <div className="grid grid-cols-2 gap-2">
                {POLICY_ACTIONS.map(action => {
                  const ActionIcon = ACTION_ICONS[action.icon] || Zap;
                  const isSelected = selectedActions.includes(action.value);
                  const isDefault = defaultActionTypes.includes(action.value);
                  const config = actionConfigs[action.value] || {};

                  return (
                    <div
                      key={action.value}
                      className="rounded-lg cursor-pointer transition-all"
                      style={{
                        backgroundColor: isSelected ? `${palette.accents.primary.base}10` : palette.borders.subtle,
                        border: `2px solid ${isSelected ? palette.accents.primary.base : palette.borders.medium}`
                      }}
                      onClick={() => onToggleAction(action.value)}
                    >
                      <div className="flex items-center gap-2 p-2.5">
                        <div
                          className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0"
                          style={{
                            borderColor: isSelected ? palette.accents.primary.base : palette.borders.medium,
                            backgroundColor: isSelected ? palette.accents.primary.base : 'transparent'
                          }}
                        >
                          {isSelected && <Check size={10} style={{ color: palette.text.inverse }} />}
                        </div>
                        <div
                          className="p-1 rounded flex-shrink-0"
                          style={{ backgroundColor: isSelected ? `${palette.accents.primary.base}20` : palette.borders.medium }}
                        >
                          <ActionIcon size={14} style={{ color: isSelected ? palette.accents.primary.base : palette.text.muted }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-sm" style={{ color: palette.text.primary }}>
                              {action.label}
                            </span>
                            {isDefault && (
                              <span
                                className="text-[10px] px-1 py-0.5 rounded"
                                style={{ backgroundColor: withOpacity(palette.accents.warm.base, 13), color: palette.accents.warm.base }}
                              >
                                ★
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Config input for actions that need it - shown inline when selected */}
                      {isSelected && action.has_config && (
                        <div
                          className="px-2.5 pb-2.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder={action.config_label}
                            value={config.value || ''}
                            onChange={(e) => onUpdateActionConfig(action.value, 'value', e.target.value)}
                            className="w-full px-2 py-1.5 rounded text-xs"
                            style={{
                              backgroundColor: graph.background,
                              border: `1px solid ${palette.borders.medium}`,
                              color: palette.text.primary
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Run Test Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => {
                  onSubTabChange('test');
                  // Small delay to let tab switch, then run test
                  setTimeout(() => onRunTest(), 100);
                }}
                disabled={selectedActions.length === 0}
                className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: selectedActions.length === 0 ? palette.borders.medium : palette.accents.primary.base,
                  color: selectedActions.length === 0 ? palette.text.muted : palette.text.inverse,
                  cursor: selectedActions.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                <Play size={16} />
                Run Test
              </button>
            </div>
          </div>
        )}

        {/* Test Tab */}
        {activeSubTab === 'test' && (
          <div className="space-y-3">
            {/* Header with Mutation Config and Policy Toggle - inline */}
            <div
              className="px-3 py-2 rounded-lg flex items-center justify-between gap-4 flex-wrap"
              style={{ backgroundColor: palette.borders.subtle, border: `1px solid ${components.card.borderColor}` }}
            >
              {/* Left: Mutation details */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-[10px] uppercase" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
                  <FlaskConical size={10} />
                  Test
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px]" style={{ color: palette.text.supporting }}>Target:</span>
                  <code
                    className="text-xs px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: palette.borders.medium, color: palette.accents.primary.base }}
                  >
                    {mutation_config?.span_path}
                  </code>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px]" style={{ color: palette.text.supporting }}>Mutation:</span>
                  <code className="text-xs" style={{ color: palette.accents.warm.base }}>{mutation_config?.mutation_type}</code>
                </div>
              </div>

              {/* Right: Policy Toggle */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <Shield size={12} style={{ color: policyEnabled ? palette.status.success : palette.text.muted }} />
                  <span className="text-[10px] font-medium" style={{ color: policyEnabled ? palette.status.success : palette.text.muted }}>
                    Policy {policyEnabled ? 'ON' : 'OFF'}
                  </span>
                </div>
                <button
                  onClick={onTogglePolicyEnabled}
                  disabled={selectedActions.length === 0}
                  className="relative w-8 h-4 rounded-full transition-colors"
                  style={{
                    backgroundColor: policyEnabled ? palette.status.success : palette.borders.medium,
                    opacity: selectedActions.length === 0 ? 0.5 : 1,
                    cursor: selectedActions.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                  title={selectedActions.length === 0 ? 'Configure actions first' : (policyEnabled ? 'Disable policy' : 'Enable policy')}
                >
                  <div
                    className="absolute top-0.5 w-3 h-3 rounded-full transition-all"
                    style={{
                      backgroundColor: palette.text.inverse,
                      left: policyEnabled ? '18px' : '2px'
                    }}
                  />
                </button>
              </div>
            </div>

            {/* Run Test Button */}
            {testStatus === 'idle' && (
              <div className="flex justify-center py-3">
                <button
                  onClick={onRunTest}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm ${components.button.primary}`}
                >
                  <Play size={14} />
                  Run Test
                </button>
              </div>
            )}

            {testStatus === 'running' && (
              <div className="flex items-center justify-center gap-3 py-4">
                <Loader2 size={16} className="animate-spin" style={{ color: palette.accents.primary.base }} />
                <span className="text-sm" style={{ color: palette.text.primary }}>
                  Running test {policyEnabled ? 'with' : 'without'} policy...
                </span>
              </div>
            )}

            {/* Test Results */}
            {testStatus === 'complete' && testResult === 'fail' && (
              <div className="space-y-3">
                {/* Mutation Trace Visualization */}
                <MutationTraceView
                  mutationConfig={mutation_config}
                  testCase={test_case}
                  clusterId={clusterId}
                  policyEnabled={false}
                  policyConfig={suggested_policy}
                  assertions={assertions}
                />

                {/* Test Result Summary */}
                <div
                  className="rounded overflow-hidden"
                  style={{ border: `1px solid ${palette.severity.critical.border}` }}
                >
                  <div className="px-3 py-2" style={{ backgroundColor: palette.borders.subtle }}>
                    <span className="text-[10px]" style={{ color: palette.text.supporting }}>Input: </span>
                    <span className="text-xs font-mono" style={{ color: palette.text.primary }}>"{test_case?.input}"</span>
                  </div>
                  <div className="px-3 py-2" style={{ backgroundColor: palette.severity.critical.bg, borderTop: `1px solid ${components.card.borderColor}` }}>
                    <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.status.danger }}>
                      <XCircle size={12} />
                      Test Failed (Issue Reproduced)
                    </div>
                  </div>
                  <div className="px-3 py-1.5 flex flex-wrap gap-1.5" style={{ backgroundColor: palette.borders.subtle, borderTop: `1px solid ${components.card.borderColor}` }}>
                    {assertions?.map((a, idx) => (
                      <span
                        key={idx}
                        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: a.passed ? palette.severity.low.bg : palette.severity.critical.bg,
                          color: a.passed ? palette.status.success : palette.status.danger
                        }}
                      >
                        {a.passed ? <CheckCircle size={8} /> : <XCircle size={8} />}
                        {a.name.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {testStatus === 'complete' && testResult === 'pass' && (() => {
              // Get the respond_gracefully action config if selected
              const gracefulResponse = selectedActions.includes('respond_gracefully')
                ? actionConfigs['respond_gracefully']?.value
                : null;
              // Get other triggered actions for display
              const triggeredActions = selectedActions
                .filter(a => a !== 'respond_gracefully')
                .map(a => POLICY_ACTIONS.find(p => p.value === a)?.label)
                .filter(Boolean);

              return (
                <div className="space-y-3">
                  {/* Mutation Trace Visualization - with policy protection shown */}
                  <MutationTraceView
                    mutationConfig={mutation_config}
                    testCase={test_case}
                    clusterId={clusterId}
                    policyEnabled={true}
                    policyConfig={suggested_policy}
                    assertions={assertions}
                  />

                  {/* Test Result Summary */}
                  <div
                    className="rounded overflow-hidden"
                    style={{ border: `1px solid ${palette.severity.low.border}` }}
                  >
                  <div className="px-3 py-2" style={{ backgroundColor: palette.borders.subtle }}>
                    <span className="text-[10px]" style={{ color: palette.text.supporting }}>Input: </span>
                    <span className="text-xs font-mono" style={{ color: palette.text.primary }}>"{test_case?.input}"</span>
                  </div>
                  <div className="px-3 py-2" style={{ backgroundColor: palette.severity.high.bg, borderTop: `1px solid ${components.card.borderColor}` }}>
                    <div className="flex items-center gap-2 text-xs" style={{ color: palette.status.warning }}>
                      <Shield size={12} />
                      <span>Policy Triggered</span>
                      <span className="text-[10px]" style={{ color: palette.text.supporting }}>
                        ({suggested_policy?.metric?.type} {suggested_policy?.metric?.comparator} {suggested_policy?.metric?.threshold})
                      </span>
                      {triggeredActions.length > 0 && (
                        <span className="text-[10px]" style={{ color: palette.text.supporting }}>
                          → {triggeredActions.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-3 py-2" style={{ backgroundColor: palette.severity.low.bg, borderTop: `1px solid ${components.card.borderColor}` }}>
                    <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.status.success }}>
                      <CheckCircle size={12} />
                      {gracefulResponse ? 'Graceful Response' : 'Test Passed'}
                    </div>
                    <div className="text-xs font-mono" style={{ color: palette.text.primary }}>
                      "{gracefulResponse || test_case?.original_output}"
                    </div>
                  </div>
                  <div className="px-3 py-1.5 flex flex-wrap gap-1.5" style={{ backgroundColor: palette.borders.subtle, borderTop: `1px solid ${components.card.borderColor}` }}>
                    {assertions?.map((a, idx) => (
                      <span
                        key={idx}
                        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: palette.severity.low.bg, color: palette.status.success }}
                      >
                        <CheckCircle size={8} />
                        {a.name.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  </div>
                </div>
              );
            })()}

            {/* Run Again button after test completes */}
            {testStatus === 'complete' && (
              <div className="flex justify-center pt-1">
                <button
                  onClick={onRunTest}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: 'transparent',
                    color: palette.accents.primary.base,
                    border: `1px solid ${palette.accents.primary.base}`
                  }}
                >
                  <Play size={12} />
                  Run Again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Simulate Tab Content - Shows prompt comparison + regression testing + MCP integration
const SimulateTabContent = ({ cluster, fixConfig, clusterId, rca, onSimulateComplete }) => {
  const [copied, setCopied] = useState(false);
  const [testStatus, setTestStatus] = useState('idle'); // idle | running | complete
  const [currentTestIndex, setCurrentTestIndex] = useState(0);
  const [testResults, setTestResults] = useState(null);
  const [expandedTests, setExpandedTests] = useState({}); // Track which test cases are expanded
  const [configCopied, setConfigCopied] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // MCP config
  const mcpConfig = `"lumoz": { "command": "lumoz-mcp", "env": { "LUMOZ_API_KEY": "id:secret" } }`;

  // Claude prompt
  const claudePrompt = `Use Lumoz MCP to fix: ${cluster?.cluster_id}
Target: ${fixConfig?.target_span}
Call get_resiliency_failures then get_recommendation_details and apply the fix.`;

  const handleCopyConfig = async () => {
    await navigator.clipboard.writeText(`{\n  "mcpServers": {\n    ${mcpConfig}\n  }\n}`);
    setConfigCopied(true);
    setTimeout(() => setConfigCopied(false), 2000);
  };

  const handleCopyPrompt = async () => {
    await navigator.clipboard.writeText(claudePrompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  };

  const toggleTestExpanded = (testId) => {
    setExpandedTests(prev => {
      // If clicking on already expanded test, close it
      if (prev[testId]) {
        return { [testId]: false };
      }
      // Otherwise, close all and open only this one
      return { [testId]: true };
    });
  };

  const handleCopy = async () => {
    const textToCopy = fixConfig.fix_type === 'config_change'
      ? fixConfig.enhanced_code
      : fixConfig.enhanced_prompt;
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Run regression tests with progress
  const handleRunTests = () => {
    if (!fixConfig?.regression_tests) return;

    setTestStatus('running');
    setCurrentTestIndex(0);
    setTestResults(null);

    const testCases = fixConfig.regression_tests.test_cases;
    let currentIdx = 0;

    const runNextTest = () => {
      if (currentIdx < testCases.length) {
        setCurrentTestIndex(currentIdx);
        currentIdx++;
        setTimeout(runNextTest, 800); // Simulate test execution time
      } else {
        // All tests complete
        setTestStatus('complete');
        setTestResults({
          test_cases: testCases,
          regression_checks: fixConfig.regression_tests.regression_checks,
          all_passed: true
        });
        // Notify parent that simulation is complete
        if (onSimulateComplete) {
          onSimulateComplete();
        }
      }
    };

    setTimeout(runNextTest, 500);
  };

  if (!fixConfig) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Zap size={32} style={{ color: palette.text.supporting }} className="mb-3 opacity-50" />
        <p className="text-sm" style={{ color: palette.text.supporting }}>
          No fix configuration available
        </p>
      </div>
    );
  }

  // Render enhanced prompt with inline enhancement markers
  const renderEnhancedPrompt = (prompt) => {
    const parts = prompt.split(/(## [A-Z][A-Z\s]+\n)/);
    return parts.map((part, idx) => {
      if (part.match(/^## [A-Z][A-Z\s]+\n$/)) {
        const headerText = part.replace('## ', '').replace('\n', '');
        return (
          <span key={idx}>
            <span
              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-1"
              style={{ backgroundColor: withOpacity(palette.status.success, 15), color: palette.status.success }}
            >
              + NEW
            </span>
            <span style={{ color: palette.status.success, fontWeight: 600 }}>## {headerText}</span>
            {'\n'}
          </span>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const regressionTests = fixConfig.regression_tests;

  return (
    <div className="space-y-4">
      {/* Header: Target Span */}
      <div
        className="p-3 rounded-lg"
        style={{ backgroundColor: palette.borders.subtle, border: `1px solid ${components.card.borderColor}` }}
      >
        <div className="flex items-center gap-2 text-xs uppercase mb-1.5" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
          <Zap size={12} />
          {fixConfig.fix_type === 'config_change' ? 'Configuration Experiment' : 'Prompt Experiment'}
        </div>
        <div>
          <span className="text-xs" style={{ color: palette.text.supporting }}>Target: </span>
          <code
            className="text-sm px-2 py-0.5 rounded"
            style={{ backgroundColor: palette.borders.medium, color: palette.accents.primary.base }}
          >
            {fixConfig.target_span}
          </code>
        </div>
      </div>

      {/* Side-by-side Code/Prompts - Collapsible when tests are running/complete */}
      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: `1px solid ${components.card.borderColor}` }}
        >
          <div
            className="px-3 py-1.5 text-xs font-medium"
            style={{ backgroundColor: palette.borders.medium, color: palette.text.supporting }}
          >
            {fixConfig.fix_type === 'config_change' ? 'Current Configuration' : 'Current Prompt'}
          </div>
          <pre
            className="p-3 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto leading-relaxed max-h-32 overflow-y-auto"
            style={{ backgroundColor: graph.background, color: palette.text.supporting }}
          >
            {fixConfig.fix_type === 'config_change' ? fixConfig.existing_code : fixConfig.existing_prompt}
          </pre>
        </div>
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: `1px solid ${palette.status.success}` }}
        >
          <div
            className="px-3 py-1.5 text-xs font-medium flex items-center justify-between"
            style={{ backgroundColor: withOpacity(palette.status.success, 8), color: palette.status.success }}
          >
            <div className="flex items-center gap-1.5">
              <CheckCircle size={12} />
              {fixConfig.fix_type === 'config_change' ? 'Recommended Configuration' : 'Enhanced Prompt'}
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors hover:opacity-80"
            >
              {copied ? <><Check size={12} /><span>Copied</span></> : <><Copy size={12} /><span>Copy</span></>}
            </button>
          </div>
          <pre
            className="p-3 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto leading-relaxed max-h-32 overflow-y-auto"
            style={{ backgroundColor: graph.background, color: palette.text.primary }}
          >
            {fixConfig.fix_type === 'config_change'
              ? fixConfig.enhanced_code
              : (fixConfig.enhanced_prompt ? renderEnhancedPrompt(fixConfig.enhanced_prompt) : '')}
          </pre>
        </div>
      </div>

      {/* Regression Testing Section */}
      {regressionTests && (
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: `1px solid ${components.card.borderColor}` }}
        >
          {/* Header */}
          <div
            className="px-3 py-2 flex items-center justify-between"
            style={{ backgroundColor: palette.borders.medium }}
          >
            <div className="flex items-center gap-2">
              <FlaskConical size={14} style={{ color: palette.accents.primary.base }} />
              <span className="text-sm font-medium" style={{ color: palette.text.primary }}>
                Regression Tests
              </span>
              <span className="text-xs" style={{ color: palette.text.supporting }}>
                ({regressionTests.test_cases.length} test cases)
              </span>
            </div>
            {testStatus === 'idle' && (
              <button
                onClick={handleRunTests}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs ${components.button.primary}`}
              >
                <Play size={12} />
                Run Tests
              </button>
            )}
            {testStatus === 'complete' && testResults?.all_passed && (
              <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: palette.status.success }}>
                <CheckCircle size={14} />
                All Tests Passed
              </div>
            )}
          </div>

          {/* Test Content */}
          <div className="p-3 space-y-3" style={{ backgroundColor: graph.background }}>
            {/* Running State */}
            {testStatus === 'running' && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm" style={{ color: palette.text.primary }}>
                  <Loader2 size={14} className="animate-spin" style={{ color: palette.accents.primary.base }} />
                  Running test {currentTestIndex + 1} of {regressionTests.test_cases.length}...
                </div>
                <div className="space-y-1">
                  {regressionTests.test_cases.map((tc, idx) => (
                    <div
                      key={tc.id}
                      className="flex items-center gap-2 text-xs py-1 px-2 rounded"
                      style={{
                        backgroundColor: idx < currentTestIndex ? palette.severity.low.bg : idx === currentTestIndex ? palette.borders.subtle : 'transparent',
                        color: idx <= currentTestIndex ? palette.text.primary : palette.text.muted
                      }}
                    >
                      {idx < currentTestIndex ? (
                        <CheckCircle size={12} style={{ color: palette.status.success }} />
                      ) : idx === currentTestIndex ? (
                        <Loader2 size={12} className="animate-spin" style={{ color: palette.accents.primary.base }} />
                      ) : (
                        <div className="w-3 h-3 rounded-full border" style={{ borderColor: palette.borders.medium }} />
                      )}
                      {tc.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Idle State */}
            {testStatus === 'idle' && (
              <div className="text-center py-4">
                <p className="text-sm" style={{ color: palette.text.supporting }}>
                  Run regression tests to verify the enhanced prompt doesn't break existing functionality.
                </p>
              </div>
            )}

            {/* Complete State - Results */}
            {testStatus === 'complete' && testResults && (
              <div className="space-y-3">
                {/* Test Cases Results - Expandable */}
                <div>
                  <div className="text-xs uppercase mb-2" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
                    Test Cases
                  </div>
                  <div className="space-y-2">
                    {testResults.test_cases.map((tc) => {
                      const isExpanded = expandedTests[tc.id];
                      return (
                        <div
                          key={tc.id}
                          className="rounded-lg overflow-hidden"
                          style={{ border: `1px solid ${palette.borders.medium}` }}
                        >
                          {/* Test Case Header - Clickable */}
                          <div
                            className="flex items-center justify-between py-2 px-3 cursor-pointer hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: palette.borders.subtle }}
                            onClick={() => toggleTestExpanded(tc.id)}
                          >
                            <div className="flex items-center gap-2">
                              <CheckCircle size={12} style={{ color: palette.status.success }} />
                              <span className="text-xs font-medium" style={{ color: palette.text.primary }}>{tc.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 text-xs" style={{ color: palette.text.supporting }}>
                                {tc.metrics.faithfulness != null ? (
                                  <span>faithfulness: {Math.round((tc.metrics.faithfulness_before || 0.85) * 100)}% → <span style={{ color: palette.status.success }}>{Math.round(tc.metrics.faithfulness * 100)}%</span></span>
                                ) : tc.metrics.context_relevancy != null ? (
                                  <span>context_relevancy: {Math.round((tc.metrics.context_relevancy_before || 0.38) * 100)}% → <span style={{ color: palette.status.success }}>{Math.round(tc.metrics.context_relevancy * 100)}%</span></span>
                                ) : null}
                                <span>latency: {tc.metrics.latency_ms}ms</span>
                              </div>
                              <div
                                className="transition-transform duration-200"
                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                              >
                                <ChevronDown size={14} style={{ color: palette.text.supporting }} />
                              </div>
                            </div>
                          </div>

                          {/* Expanded Content - Trace View with smooth transition */}
                          <div
                            className="grid transition-all duration-300 ease-in-out"
                            style={{
                              gridTemplateRows: isExpanded ? '1fr' : '0fr',
                              opacity: isExpanded ? 1 : 0
                            }}
                          >
                            <div className="overflow-hidden">
                              <div className="p-3" style={{ backgroundColor: graph.background }}>
                                <EnhancementTraceView
                                  fixConfig={fixConfig}
                                  testCase={tc}
                                  clusterId={clusterId}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Regression Checks */}
                <div>
                  <div className="text-xs uppercase mb-2" style={{ color: palette.text.muted, letterSpacing: '0.05em' }}>
                    Regression Checks
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {testResults.regression_checks.map((check, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-1.5 px-2 py-1 rounded text-xs"
                        style={{
                          backgroundColor: check.status === 'pass' ? palette.severity.low.bg : palette.severity.critical.bg,
                          border: `1px solid ${check.status === 'pass' ? palette.severity.low.border : palette.severity.critical.border}`
                        }}
                      >
                        {check.status === 'pass' ? (
                          <CheckCircle size={10} style={{ color: palette.status.success }} />
                        ) : (
                          <XCircle size={10} style={{ color: palette.status.danger }} />
                        )}
                        <span style={{ color: palette.text.primary }}>{check.name.replace(/_/g, ' ')}</span>
                        <span style={{ color: palette.text.supporting }}>{check.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Divider */}
      <div
        className="my-6 border-t"
        style={{ borderColor: components.card.borderColor }}
      />

      {/* Apply with Claude Code Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ExternalLink size={16} style={{ color: palette.accents.primary.base }} />
          <h3 className="text-sm font-semibold" style={{ color: palette.text.primary }}>
            Apply with Claude Code
          </h3>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-2 gap-4">
          {/* Step 1: MCP Setup */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: palette.accents.primary.base, color: palette.text.inverse }}
              >
                1
              </div>
              <span className="text-xs font-medium" style={{ color: palette.text.primary }}>
                Configure MCP Server
              </span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ backgroundColor: palette.borders.subtle, color: palette.text.supporting }}
              >
                One-time
              </span>
            </div>
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: `1px solid ${components.card.borderColor}` }}
            >
              <div
                className="px-3 py-1.5 text-xs flex items-center justify-between"
                style={{ backgroundColor: palette.borders.medium, color: palette.text.supporting }}
              >
                <span>~/.claude/settings.json</span>
                <button
                  onClick={handleCopyConfig}
                  className="flex items-center gap-1 hover:opacity-80"
                >
                  {configCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
              <pre
                className="p-2 text-[11px] font-mono"
                style={{ backgroundColor: graph.background, color: palette.text.primary }}
              >
                {mcpConfig}
              </pre>
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: palette.text.supporting }}>
              <code className="px-1 py-0.5 rounded" style={{ backgroundColor: palette.borders.medium }}>pip install lumoz-sdk[mcp]</code>
            </p>
          </div>

          {/* Step 2: Apply with Claude Code */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: palette.accents.primary.base, color: palette.text.inverse }}
              >
                2
              </div>
              <span className="text-xs font-medium" style={{ color: palette.text.primary }}>
                Run in Claude Code
              </span>
            </div>
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: `1px solid ${palette.accents.primary.base}` }}
            >
              <div
                className="px-3 py-1.5 text-xs flex items-center justify-between"
                style={{ backgroundColor: withOpacity(palette.accents.primary.base, 8), color: palette.accents.primary.base }}
              >
                <span>Prompt</span>
                <button
                  onClick={handleCopyPrompt}
                  className="flex items-center gap-1 hover:opacity-80"
                >
                  {promptCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
              <pre
                className="p-2 text-[11px] font-mono whitespace-pre-wrap leading-relaxed"
                style={{ backgroundColor: graph.background, color: palette.text.primary }}
              >
                {claudePrompt}
              </pre>
            </div>
          </div>
        </div>

        {/* Available MCP Tools - inline */}
        <div
          className="px-3 py-2 rounded-lg flex items-center gap-3"
          style={{ backgroundColor: palette.borders.subtle, border: `1px solid ${components.card.borderColor}` }}
        >
          <span className="text-[10px] font-medium" style={{ color: palette.text.supporting }}>MCP Tools:</span>
          <div className="flex gap-2 text-[10px]" style={{ color: palette.text.supporting }}>
            <code className="px-1.5 py-0.5 rounded" style={{ backgroundColor: palette.borders.medium, color: palette.accents.primary.base }}>list_resiliency_runs</code>
            <code className="px-1.5 py-0.5 rounded" style={{ backgroundColor: palette.borders.medium, color: palette.accents.primary.base }}>get_resiliency_failures</code>
            <code className="px-1.5 py-0.5 rounded" style={{ backgroundColor: palette.borders.medium, color: palette.accents.primary.base }}>get_recommendation_details</code>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tab Button
const TabButton = ({ label, isActive, onClick, icon: Icon, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-2 px-2.5 py-1 rounded-full text-sm font-medium transition-colors ${
      isActive
        ? 'bg-accent-primary-base text-text-inverse'
        : 'bg-background-tertiary text-text-supporting hover:bg-background-quaternary'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    {Icon && <Icon size={14} />}
    {label}
  </button>
);

// Get action button config based on current state
const getActionConfig = (activeTab, rcaStatus, testStatus, testResult, simulateComplete) => {
  // RCA tab
  if (activeTab === 'rca') {
    if (rcaStatus === 'idle') {
      return { label: 'Run Analysis', icon: Search, action: 'runRCA', disabled: false };
    }
    if (rcaStatus === 'running') {
      return { label: 'Analyzing...', icon: Loader2, action: null, disabled: true, loading: true };
    }
    if (rcaStatus === 'complete') {
      return { label: 'Continue to Simulate', icon: ArrowRight, action: 'goSimulate', disabled: false };
    }
  }

  // Simulate tab - show button to continue to Guard
  if (activeTab === 'fix') {
    if (simulateComplete) {
      return { label: 'Continue to Guard', icon: ArrowRight, action: 'goGuard', disabled: false };
    }
    return null;
  }

  // Guard tab - show Done when test passes (policy verified)
  if (activeTab === 'reproduce') {
    if (testStatus === 'complete' && testResult === 'pass') {
      return { label: 'Done', icon: Check, action: 'done', disabled: false };
    }
    return null;
  }

  return null;
};

// Main Modal Component
const ClusterAnalysisModal = ({ isOpen, onClose, cluster }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('rca');

  // Context panel state
  const [contextExpanded, setContextExpanded] = useState(true);

  // RCA state
  const [rcaStatus, setRcaStatus] = useState('idle'); // idle | running | complete
  const [rcaStep, setRcaStep] = useState(0);

  // Simulate state - track when simulation/regression tests complete
  const [simulateComplete, setSimulateComplete] = useState(false);

  // Guard (Reproduce) state - new tabbed design
  const [selectedActions, setSelectedActions] = useState([]);
  const [actionConfigs, setActionConfigs] = useState({});
  const [policyEnabled, setPolicyEnabled] = useState(false);
  const [testStatus, setTestStatus] = useState('idle'); // idle | running | complete
  const [testResult, setTestResult] = useState(null); // null | 'pass' | 'fail'
  const [guardSubTab, setGuardSubTab] = useState('test'); // test | configure

  // Get data for this cluster (needed early for default actions)
  const reproConfig = getReproductionConfig(cluster?.cluster_id);

  // Reset state when modal opens with new cluster
  React.useEffect(() => {
    if (isOpen && cluster) {
      setActiveTab('rca');
      setContextExpanded(true);
      setRcaStatus('idle');
      setRcaStep(0);
      setSimulateComplete(false);
      // Initialize with default actions from suggested policy
      const defaultActions = reproConfig?.suggested_policy?.default_actions || [];
      setSelectedActions(defaultActions.map(a => a.type));
      // Initialize configs from default actions
      const configs = {};
      defaultActions.forEach(a => {
        if (a.config) configs[a.type] = a.config;
      });
      setActionConfigs(configs);
      setPolicyEnabled(false);
      setTestStatus('idle');
      setTestResult(null);
      setGuardSubTab('test');
    }
  }, [isOpen, cluster?.cluster_id]);

  if (!isOpen || !cluster) return null;

  // Get other data for this cluster
  const rca = getRCAResult(cluster.cluster_id);
  const fixConfig = getFixConfig(cluster.cluster_id);

  // Run RCA analysis with progressive steps
  const handleRunRCA = () => {
    setContextExpanded(false);
    setRcaStatus('running');
    setRcaStep(0);

    // Progress through each step
    let currentStep = 0;
    const progressStep = () => {
      if (currentStep < RCA_STEPS.length - 1) {
        currentStep++;
        setRcaStep(currentStep);
        setTimeout(progressStep, RCA_STEPS[currentStep].duration);
      } else {
        // Complete after last step
        setTimeout(() => {
          setRcaStatus('complete');
        }, RCA_STEPS[currentStep].duration);
      }
    };

    // Start first step
    setTimeout(progressStep, RCA_STEPS[0].duration);
  };

  // Toggle action selection
  const handleToggleAction = (actionType) => {
    setSelectedActions(prev => {
      if (prev.includes(actionType)) {
        return prev.filter(a => a !== actionType);
      } else {
        return [...prev, actionType];
      }
    });
    // Reset test when actions change
    setTestStatus('idle');
    setTestResult(null);
  };

  // Update action config
  const handleUpdateActionConfig = (actionType, configKey, configValue) => {
    setActionConfigs(prev => ({
      ...prev,
      [actionType]: { ...prev[actionType], [configKey]: configValue }
    }));
  };

  // Toggle policy enabled
  const handleTogglePolicyEnabled = () => {
    setPolicyEnabled(prev => !prev);
    // Reset test when policy toggle changes
    setTestStatus('idle');
    setTestResult(null);
  };

  // Run test (result depends on whether policy is enabled)
  const handleRunTest = () => {
    setTestStatus('running');
    setTimeout(() => {
      setTestStatus('complete');
      // If policy is enabled, test passes; otherwise, test fails
      setTestResult(policyEnabled ? 'pass' : 'fail');
    }, 2000);
  };

  // Tab accessibility
  const rcaComplete = rcaStatus === 'complete';
  // Get action button config
  const actionConfig = getActionConfig(activeTab, rcaStatus, testStatus, testResult, simulateComplete);
  const ActionIcon = actionConfig?.icon;

  // Handle action button click
  const handleAction = () => {
    switch (actionConfig.action) {
      case 'runRCA':
        handleRunRCA();
        break;
      case 'goSimulate':
        setActiveTab('fix');
        break;
      case 'goGuard':
        setActiveTab('reproduce');
        break;
      case 'done':
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col rounded-lg"
        style={{ backgroundColor: graph.background, border: `1px solid ${components.card.borderColor}` }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4" style={{ borderBottom: `1px solid ${components.card.borderColor}` }}>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold" style={{ color: palette.text.primary }}>
                {cluster.name}
              </h2>
              <span
                className="px-2 py-0.5 rounded text-xs"
                style={{
                  backgroundColor: cluster.severity === 'high'
                    ? palette.severity.critical.bg
                    : cluster.severity === 'medium'
                    ? palette.severity.high.bg
                    : palette.severity.low.bg,
                  color: cluster.severity === 'high'
                    ? palette.severity.critical.text
                    : cluster.severity === 'medium'
                    ? palette.severity.high.text
                    : palette.severity.low.text
                }}
              >
                {cluster.severity?.toUpperCase()}
              </span>
              <span className="text-sm" style={{ color: palette.text.supporting }}>
                {cluster.trace_count} traces
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded transition-colors"
            style={{ color: palette.text.supporting }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden p-6">
          {/* Context Panel - Always visible at top */}
          <div className="flex-shrink-0 mb-6">
            <ContextPanel
              cluster={cluster}
              isExpanded={contextExpanded}
              onToggle={() => setContextExpanded(!contextExpanded)}
            />
          </div>

          {/* Tabs - Fixed */}
          <div
            className="flex-shrink-0 flex gap-2 mb-4"
          >
            <TabButton
              label="RCA"
              icon={Search}
              isActive={activeTab === 'rca'}
              onClick={() => setActiveTab('rca')}
            />
            <TabButton
              label="Simulate"
              icon={Zap}
              isActive={activeTab === 'fix'}
              onClick={() => rcaComplete && setActiveTab('fix')}
              disabled={!rcaComplete}
            />
            <TabButton
              label="Guard"
              icon={Shield}
              isActive={activeTab === 'reproduce'}
              onClick={() => simulateComplete && setActiveTab('reproduce')}
              disabled={!simulateComplete}
            />
          </div>

          {/* Tab Content - Scrollable */}
          <div
            className="flex-1 overflow-y-auto p-4 rounded-b-lg"
            style={{ backgroundColor: palette.borders.subtle, border: `1px solid ${components.card.borderColor}`, borderTop: 'none' }}
          >
            {activeTab === 'rca' && (
              <RCATabContent
                rca={rca}
                rcaStatus={rcaStatus}
                rcaStep={rcaStep}
              />
            )}
            {activeTab === 'fix' && (
              <SimulateTabContent
                cluster={cluster}
                fixConfig={fixConfig}
                clusterId={cluster?.cluster_id}
                rca={rca}
                onSimulateComplete={() => setSimulateComplete(true)}
              />
            )}
            {activeTab === 'reproduce' && (
              <ReproduceTabContent
                reproConfig={reproConfig}
                clusterId={cluster?.cluster_id}
                selectedActions={selectedActions}
                actionConfigs={actionConfigs}
                onToggleAction={handleToggleAction}
                onUpdateActionConfig={handleUpdateActionConfig}
                policyEnabled={policyEnabled}
                onTogglePolicyEnabled={handleTogglePolicyEnabled}
                testStatus={testStatus}
                testResult={testResult}
                onRunTest={handleRunTest}
                activeSubTab={guardSubTab}
                onSubTabChange={setGuardSubTab}
              />
            )}
          </div>
        </div>

        {/* Actions Panel - only show if there's an action */}
        {actionConfig && (
          <div
            className="flex items-center justify-between gap-3 px-6 py-4"
            style={{ borderTop: `1px solid ${components.card.borderColor}` }}
          >
            {/* Left: Info message on Guard tab */}
            {activeTab === 'reproduce' ? (
              <p className="text-xs" style={{ color: palette.text.supporting }}>
                This analysis is saved in <span style={{ color: palette.text.primary }}>RCA</span> and the policy in <span style={{ color: palette.text.primary }}>Policies</span>.
              </p>
            ) : (
              <div />
            )}

            {/* Right: Action button */}
            <button
              onClick={handleAction}
              disabled={actionConfig.disabled}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                actionConfig.disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: palette.accents.primary.base,
                color: palette.text.inverse
              }}
            >
              <ActionIcon size={16} className={actionConfig.loading ? 'animate-spin' : ''} />
              {actionConfig.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClusterAnalysisModal;
